/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Table, Switch, TreeSelect, message, Modal, Spin } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TableProps } from 'antd/lib/table';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import _ from 'lodash';
import { queryCache, useMutation, useQuery } from 'react-query';

import IconButton from '~components/_common/IconButton/IconButton';
import { CreateEditTenderUserModal } from '~components/popups-modals/CreateEditTenderUser.modal';
import { DeleteTenderUserModal } from '~components/popups-modals/DeleteTenderUser.modal';
import { IconPopup } from '~components/popups-modals/DetailsLayouts/components';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { fadeInKeyframes } from '~src/styles/animations';
import { DB_STATE } from '~src/types/enums';
import { useTenderUsers } from '~src/hooks/api-hooks/useTenderUsers';
import { DbTenderUser } from '~src/types/db';
import { useUserSettings } from '~src/hooks/api-hooks/useUserSettings';
import { useArchiveUsers } from '~src/hooks/api-hooks/useArchiveUsers';
import {
  addTenderUser,
  getTenderSettings,
  updateTenderSettings,
} from '~src/services/api/tender';
import { useProjectId } from '~src/hooks/useProjectId';
import { useIsSuperuser } from '~src/hooks/useIsSuperuser';
import { tabStyleFn } from './tabs.util';

const { confirm } = Modal;

export interface TenderTabProps {
  loading?: boolean;
}

export const TenderTab: React.FC<TenderTabProps> = ({
  loading,
  projectData,
}) => {
  const intl = useIntl();
  const style = useStyles(styleFn);
  const tabStyles = useStyles(tabStyleFn);

  const tenderUsers = useTenderUsers();
  const isSuperuser = useIsSuperuser();

  const archiveUsers = useArchiveUsers();
  const userSettings = useUserSettings();
  const projectId = useProjectId();

  const isTenderAdmin = projectData?.is_tender_admin;

  // --- API: Tender Settings (Toggle) ---
  const { data: tenderSettings, isLoading: isLoadingSettings } = useQuery(
    ['tenderSettings', projectId],
    () => getTenderSettings({ projectId: String(projectId) }),
    {
      enabled: !!projectId,
      refetchOnWindowFocus: false,
    }
  );

  const [mutateTenderSettings, { isLoading: isUpdatingSettings }] = useMutation(
    updateTenderSettings,
    {
      onSuccess: () => {
        message.success(intl.formatMessage({ id: 'tender-module-enabled' }));
        queryCache.invalidateQueries(['tenderSettings', projectId]);
        // Ideally invalidate project settings as well if it affects other tabs
        queryCache.invalidateQueries('archiveSettings');
      },
      onError: () => {
        message.error(intl.formatMessage({ id: 'tender-update-failed' }));
      },
    }
  );

  // --- Permission Check ---
  const canEdit = tenderSettings?.system_enabled;

  const handleSwitchChange = (checked: boolean) => {
    // If user tries to turn it off, strictly ignore it (UI should be disabled, but extra safety here)
    if (!checked) return;

    confirm({
      title: intl.formatMessage({ id: 'enable-tender-header' }),
      content: intl.formatMessage({ id: 'enable-tender-content' }),
      okText: intl.formatMessage({ id: 'activate-tender' }),
      cancelText: intl.formatMessage({ id: 'cancel' }),
      onOk() {
        mutateTenderSettings({
          projectId: String(projectId),
          project_enabled: true,
        });
      },
      onCancel() {
        //
      },
    });
  };

  // --- Data Preparation ---

  const tableData = tenderUsers.data;
  const userSettingsData = userSettings.data;
  const currentUserId = userSettingsData?.user_id;

  const tenderAdminData = useMemo(() => {
    return tableData
      ?.map((item) => ({ ...item, key: item.id }))
      ?.filter((item) => item.tender_user_type === 1);
  }, [tableData]);

  const tenderManagerData = useMemo(() => {
    return tableData
      ?.map((item) => ({ ...item, key: item.id }))
      ?.filter((item) => item.tender_user_type === 0);
  }, [tableData]);

  const [createEditModalVisible, setCreateEditModalVisible] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedValueAdmin, setSelectedValueAdmin] = useState<
    string | number | undefined
  >();
  const [selectedValueManager, setSelectedValueManager] = useState<
    string | number | undefined
  >();

  const [selectedUser, setSelectedUser] = useState<DbTenderUser | null>(null);

  const [doAddTenderUser] = useMutation(addTenderUser, {
    onError: () => {
      message.error(intl.formatMessage({ id: 'add-user-error' }));
    },
    onSuccess: () => {
      queryCache.invalidateQueries('tenderUsersList');
      queryCache.invalidateQueries('tenderArchiveList');
      message.success(intl.formatMessage({ id: 'add-user-success' }));

      setSelectedValueAdmin(undefined);
      setSelectedValueManager(undefined);
    },
  });

  const handleTreeSelectChangeAdmin = (
    value: string | number,
    action: string
  ) => {
    setSelectedValueAdmin(value);
    doAddTenderUser({
      userId: String(value),
      action,
      projectId: String(projectId),
    });
  };

  const addTenderAdminData = useMemo(() => {
    const groupedUsers = _.groupBy(
      archiveUsers,
      (user) => user.workgroup.group_initials
    );

    return _.map(groupedUsers, (users, groupInitials) => {
      return {
        title: (
          <div>
            <span style={{ color: '#c1c1c1', fontSize: '12px' }}>
              {groupInitials + ' - ' + users[0].workgroup.group_name}
            </span>
          </div>
        ),
        value: groupInitials,
        selectable: false,
        children: users.map((user) => {
          const userFromTable = tableData?.find((item) => item.id === user.id);
          const selectable = !tenderAdminData?.some(
            (item) => item.id === user.id
          );
          return {
            title: (
              <div>
                <span style={{ color: selectable ? '' : 'grey' }}>
                  {user.user_initials + ' - ' + user.full_name}{' '}
                </span>
                {userFromTable?.tender_user_type !== undefined && (
                  <FontAwesomeIcon
                    color={'#c1c1c1'}
                    size={'sm'}
                    icon={[
                      'far',
                      userFromTable?.tender_user_type === 1
                        ? 'user-tie'
                        : 'user-cog',
                    ]}
                  />
                )}
              </div>
            ),
            selectable,
            value: user.id,
          };
        }),
      };
    });
  }, [archiveUsers, tenderAdminData, tableData]);

  const getColorRow = (record: any) =>
    Number(record.state) === DB_STATE.REMOVED ? '#ffd4d9' : '';

  const filterFunc = (inputValue: string, treeNode: any) => {
    if (inputValue && treeNode?.value) {
      return (
        treeNode.value
          .toString()
          .toLowerCase()
          .includes(inputValue.toLowerCase()) ?? false
      );
    }
    return false;
  };

  const columns: TableProps<DbTenderUser>['columns'] = [
    {
      key: 'tagStructure',
      title: '',
      dataIndex: 'remove',
      width: 24,
      onCell: (record) => ({
        style: {
          whiteSpace: 'nowrap',
          overflow: 'auto',
          background: getColorRow(record),
          textAlign: 'center',
        },
      }),
      render: (title, node) => {
        const currentUserId = userSettingsData?.user_id;

        const isSelf = currentUserId !== undefined && currentUserId === node.id;

        // TenderAdmin can't remove own rights (superuser can bypass)
        const isDeleteDisabled = !canEdit || (isSelf && isTenderAdmin);
        // disabled={userSettingsData?.user_id === node.id && !isSuperuser} old logic

        return (
          <div
            onClick={(e) => {
              e.stopPropagation();
              if (isDeleteDisabled) return;
              setDeleteModal(true);
              setSelectedUser(node);
            }}
            css={style.actionContainer}>
            <IconButton
              disabled={isDeleteDisabled}
              icon={isSelf ? ['far', 'check'] : ['far', 'circle-xmark']}
              customStyle={[tabStyles.folderIcon, style.redIcon]}
              containerStyle={style.iconRemove}
              backgroundOff
              tooltip={intl.formatMessage({ id: 'delete-tender-user' })}
            />
          </div>
        );
      },
    },
    {
      key: 'tagStructureTitle',
      title: intl.formatMessage({ id: 'workgroup' }),
      dataIndex: 'workgroup',
      width: 80,
      onCell: (record) => ({
        style: {
          whiteSpace: 'nowrap',
          overflow: 'auto',
          background: getColorRow(record),
        },
      }),
      render: (title) => <span color={'#1f5fa6'}>{title.group_initials}</span>,
    },
    {
      key: 'tenderStructureInitials',
      title: intl.formatMessage({ id: 'initials' }),
      dataIndex: 'user_initials',
      width: 80,
      onCell: (record) => ({
        style: {
          whiteSpace: 'nowrap',
          overflow: 'auto',
          background: getColorRow(record),
        },
      }),
      render: (title) => <span color={'#1f5fa6'}>{title}</span>,
    },
    {
      key: 'tenderStructureName',
      title: intl.formatMessage({ id: 'name' }),
      dataIndex: 'full_name',
      width: 80,
      onCell: (record) => ({
        style: {
          whiteSpace: 'nowrap',
          overflow: 'auto',
          background: getColorRow(record),
        },
      }),
      render: (title) => <span color={'#1f5fa6'}>{title}</span>,
    },
  ];

  const isTenderEnabled = tenderSettings?.project_enabled;

  return (
    <div css={style.container}>
      <div css={style.rowContainer}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
          }}>
          <h3 css={style.heading}>
            {intl.formatMessage({ id: 'tender-settings' })}
          </h3>
          <IconPopup
            customStyle={{ fontSize: '130%' }}
            headerId={'tender-settings-help-header'}
            messageId={'tender-settings-help-body'}
          />
        </div>
        <span css={{ display: 'inline-block', width: 170 }}>
          {intl.formatMessage({ id: 'enable-tender' })}
        </span>

        {isLoadingSettings ? (
          <Spin />
        ) : (
          <Switch
            checked={isTenderEnabled}
            disabled={
              isLoadingSettings ||
              isUpdatingSettings ||
              isTenderEnabled ||
              !isTenderAdmin
            }
            onChange={handleSwitchChange}
            loading={isUpdatingSettings}
          />
        )}

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            margin: '20px 0 10px 0',
          }}>
          <span css={{ display: 'inline-block', width: 170 }}>
            <span css={{ fontSize: '16px', marginRight: '6px' }}>
              {intl.formatMessage({ id: 'tender-admin' })}
            </span>
            <FontAwesomeIcon
              color={'#c1c1c1'}
              size={'1x'}
              icon={['far', 'user-tie']}
            />
          </span>
          <TreeSelect
            filterTreeNode={filterFunc}
            showSearch
            className="my-tree-select"
            style={{ width: 300 }}
            dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
            treeData={addTenderAdminData}
            placeholder={intl.formatMessage({ id: 'add-user' })}
            treeDefaultExpandAll
            value={selectedValueAdmin}
            onChange={(e) =>
              handleTreeSelectChangeAdmin(e, 'set_user_as_tender_admin')
            }
          />
        </div>
        <div css={[style.rowContainer]}>
          <Table<DbTenderUser>
            showSorterTooltip={false}
            bordered
            pagination={false}
            scroll={{ y: '22vh', x: 200 }}
            loading={loading}
            columns={columns}
            dataSource={tenderAdminData}
          />
        </div>
      </div>
      {/* JIRA MVP: Tender Manager comment out */}
      {/*<div*/}
      {/*  style={{*/}
      {/*    display: 'flex',*/}
      {/*    justifyContent: 'space-between',*/}
      {/*    alignItems: 'center',*/}
      {/*    margin: '25px 0 10px 0',*/}
      {/*  }}>*/}
      {/*  <span css={{ display: 'inline-block', width: 170 }}>*/}
      {/*    <span css={{ fontSize: '16px', marginRight: '6px' }}>*/}
      {/*      {intl.formatMessage({ id: 'tender-manager' })}*/}
      {/*    </span>*/}
      {/*    <FontAwesomeIcon*/}
      {/*      color={'#c1c1c1'}*/}
      {/*      size={'1x'}*/}
      {/*      icon={['far', 'user-cog']}*/}
      {/*    />*/}
      {/*  </span>*/}
      {/*  <TreeSelect*/}
      {/*    filterTreeNode={filterFunc}*/}
      {/*    showSearch*/}
      {/*    className="my-tree-select"*/}
      {/*    style={{ width: 300 }}*/}
      {/*    dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}*/}
      {/*    treeData={addTenderManagerData}*/}
      {/*    placeholder={intl.formatMessage({ id: 'add-user' })}*/}
      {/*    treeDefaultExpandAll*/}
      {/*    value={selectedValueManager}*/}
      {/*    onChange={(e) =>*/}
      {/*      handleTreeSelectChangeManager(e, 'set_user_as_tender_manager')*/}
      {/*    }*/}
      {/*  />*/}
      {/*</div>*/}
      {/*<div css={[style.rowContainer]}>*/}
      {/*  <Table<DbTenderUser>*/}
      {/*    showSorterTooltip={false}*/}
      {/*    bordered*/}
      {/*    pagination={false}*/}
      {/*    scroll={{ y: '22vh', x: 200 }}*/}
      {/*    loading={loading}*/}
      {/*    columns={columns}*/}
      {/*    dataSource={tenderManagerData}*/}
      {/*  />*/}
      {/*</div>*/}
      {createEditModalVisible && (
        <CreateEditTenderUserModal
          visible={createEditModalVisible}
          user={selectedUser}
          onCancel={() => {
            setCreateEditModalVisible(false);
            setSelectedUser(null);
          }}
        />
      )}
      <DeleteTenderUserModal
        visible={deleteModal}
        user={selectedUser}
        onCancel={() => setDeleteModal(false)}
        onDone={() => {
          setDeleteModal(false);
          setSelectedUser(null);
        }}
      />
    </div>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  container: {},
  iconRemove: {},
  rowContainer: { marginBottom: '8px' },
  tag: {
    marginRight: 3,
    animation: `${fadeInKeyframes} 0.2s ease-out`,
  },
  actionContainer: {
    cursor: 'pointer',
  },
  containerSearch: {
    // display: 'flex',
    // alignItems: 'center',
    // border: `1px solid ${theme.colors.lightGray}`,
    // borderRadius: '2px',
    // margin: '8px 0',
    // paddingLeft: '5px',
    // width: '55%',
    // height: '32px',
    // ':focus': {
    //   border: `1px solid ${theme.colors.primary}`,
    // },
    // // backgroundColor: theme.colors.gray,
  },
  icons: {
    color: theme.colors.text.primary,
    fontSize: '16px',
  },
  inputField: {
    // border: 'none!important',
    // height: '100%',
    // width: '100%',
    // color: `${theme.colors.text.primary}`,
    //
    // '::placeholder': {
    //   color: `${theme.colors.text.secondary}`,
    // },
    //
    // ':focus': {
    //   outline: 'none',
    // },
  },
  buttonDiv: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    type: 'button',
    cursor: 'pointer',
    marginLeft: 4,
    padding: '2px 10px',
    ':hover': {
      backgroundColor: theme.colors.gray,
      borderRadius: 9,
    },
  },
  iconPlus: {
    fontSize: '20px',
    color: theme.colors.primary,
    transition: '0.3s',
  },
  whiteHeader: {
    color: 'white',
  },
  blackIcon: {
    color: theme.colors.darkGray,
  },
  redIcon: {
    color: theme.colors.red,
  },
  editColor: {
    color: theme.colors.darkGray,
    ':hover': {
      color: theme.colors.primary,
    },
  },
  editIcon: {
    fontSize: 18,
    marginTop: -7,
  },
  heading: {
    fontSize: '18px',
    fontWeight: 600,
    marginBottom: '15px',
  },
});
